import React, { useState } from "react";
import {
  Box,
  Typography,
  Grid,
  TextField,
  IconButton,
  MenuItem,
  Button,
  Snackbar,
} from "@mui/material";
import MuiAlert, { AlertProps } from "@mui/material/Alert";
import { AlertColor } from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import axios from "axios";
const BACKEND_BASE_URL = import.meta.env.VITE_BACKEND_BASE_URL;
import { useThemeContextChange } from "../themeContext";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import CallOutlinedIcon from "@mui/icons-material/CallOutlined";

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
  }
);

interface FormData {
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  subject: string;
}

interface FormErrors {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone_number?: string;
}

interface AlertState {
  open: boolean;
  message: string;
  severity: AlertColor;
}

const ContactUsTheme: React.FunctionComponent = () => {
  const {
    contentBackgroundColor,
    contentTitleColor,
    contentTextColor,
    contentButtonColor,
    contentButtonTextColor,
  } = useThemeContextChange();
  const [formData, setFormData] = useState<FormData>({
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    subject: "",
  });

  const [formErrors, setFormErrors] = useState<FormErrors>({});

  const [alert, setAlert] = useState<AlertState>({
    open: false,
    message: "",
    severity: "error",
  });

  const validateEmail = (email: string) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const validateField = (name: string, value: string) => {
    let error = "";

    switch (name) {
      case "first_name":
        if (!value) error = "Please enter first name.";
        break;
      case "last_name":
        if (!value) error = "Please enter last name.";
        break;
      case "email":
        if (!validateEmail(value))
          error = "Please enter a valid email address.";
        break;
      case "phone_number":
        if (!value) error = "Please enter contact phone number.";
        break;
      default:
        break;
    }

    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [name]: error,
    }));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    validateField(name, value);
  };

  const validateForm = () => {
    const errors: FormErrors = {};

    if (!formData.first_name.trim()) {
      errors.first_name = "Please enter first name.";
    }
    if (!formData.last_name.trim()) {
      errors.last_name = "Please enter last name.";
    }
    if (!formData.phone_number.trim()) {
      errors.phone_number = "Please enter contact phone number.";
    }
    if (!validateEmail(formData.email)) {
      errors.email = "Please enter a valid email address.";
    }

    // Set the errors state
    setFormErrors(errors);

    // Return false if there are any errors, true otherwise
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    try {
      await axios.post(
        `${BACKEND_BASE_URL}contact_us/v1/form_submit`,
        formData
      );

      setAlert({
        open: true,
        message: "Form submitted successfully!",
        severity: "success",
      });
      setFormData({
        first_name: "",
        last_name: "",
        email: "",
        phone_number: "",
        subject: "",
      });
    } catch (error) {
      console.log(error);
      setAlert({
        open: true,
        message:
          "An error occurred while submitting the form. Please try again.",
        severity: "error",
      });
    }
  };

  const handleClose = (e: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    setAlert({ ...alert, open: false });
    e.preventDefault();
  };
  return (
    <Box
      className="theme_contact"
      display="flex"
      flexDirection="column"
      alignItems="center"
      p={6}
      sx={{
        backgroundColor: `${contentBackgroundColor}!important`,
        transition: "background-color 0.5s ease",
        boxShadow: "none ",
      }}
    >
      <Grid container spacing={3} mt={2} maxWidth="lg" className="contact-form">
        <Grid item md={6} xs={12} pb={4}>
          <Typography
            variant="h5"
            mb={3}
            sx={{
              textAlign: "left",
              fontWeight: "600",
              color: contentTitleColor,
            }}
          >
            Contact Us
          </Typography>
          <Typography
            mt={2}
            mb={3}
            sx={{ color: contentTextColor, fontSize: "1rem" }}
          >
            Have questions or need more information? <br></br>
            This is where it all starts!
          </Typography>
          <Typography mb={3} sx={{ color: contentTextColor, fontSize: "1rem" }}>
            Do you want to book a discovery call? Please click here to{" "}
            <a
              href="your-link-here"
              style={{ color: contentTextColor, textDecoration: "underline" }}
            >
              Book a Discovery Call
            </a>
          </Typography>
          <Typography mb={3} sx={{ color: contentTextColor, fontSize: "1rem" }}>
            If you are looking to start a project please go to our{" "}
            <a
              href="your-link-here"
              style={{ color: contentTextColor, textDecoration: "underline" }}
            >
              Start a Project Page
            </a>{" "}
            to provide more information from our product specialists.
          </Typography>
          <Typography mb={3} sx={{ color: contentTextColor, fontSize: "1rem" }}>
            If you are looking to apply for employment please use our{" "}
            <a
              href="your-link-here"
              style={{ color: contentTextColor, textDecoration: "underline" }}
            >
              Careers page
            </a>{" "}
            to apply for an open position.
          </Typography>
          <Snackbar
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            open={alert.open}
            autoHideDuration={3000}
            onClose={handleClose}
          >
            <Alert
              variant="filled"
              onClose={handleClose}
              severity={alert.severity}
            >
              {alert.message}
            </Alert>
          </Snackbar>
          <Stack direction="row" spacing={1} sx={{ marginTop: "2%" }}>
            <Box
              sx={{
                maxWidth: {
                  sm: "25px !important",
                },
                height: "25px !important",
                backgroundColor: contentButtonColor,
                borderRadius: "15px",
              }}
              component="span"
              // src={PhoneImg}
            >
              <IconButton
                sx={{
                  padding: "5px",
                  color: contentButtonTextColor,
                  borderRadius: "15px",
                }}
              >
                <EmailOutlinedIcon sx={{ fontSize: "16px" }} />
              </IconButton>
            </Box>
            <Typography sx={{ color: contentTextColor }}>
              Test@gmail.com
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} sx={{ marginTop: "2%" }}>
            <Box
              sx={{
                maxWidth: {
                  sm: "25px !important",
                },
                height: "25px !important",
                backgroundColor: contentButtonColor,
                borderRadius: "15px",
              }}
              component="span"
              // src={PhoneImg}
            >
              <IconButton
                sx={{
                  padding: "5px",
                  color: contentButtonTextColor,
                  borderRadius: "15px",
                }}
              >
                <CallOutlinedIcon sx={{ fontSize: "16px" }} />
              </IconButton>
            </Box>
            <Typography sx={{ color: contentTextColor }}>123456789</Typography>
          </Stack>
        </Grid>
        <Grid item xs={12} md={6} pb={5}>
          <Typography
            variant="h5"
            mb={3}
            sx={{
              textAlign: "left",
              fontWeight: "600",
              color: contentTitleColor,
            }}
          >
            Get Started
          </Typography>
          <Typography sx={{ color: contentTextColor }}>
            Please get in touch with our team using the form below. We’ll follow
            up with you directly to answer and questions you may have.
          </Typography>
          <form noValidate autoComplete="off" onSubmit={handleSubmit}>
            <TextField
              label="First Name*"
              variant="outlined"
              fullWidth
              margin="normal"
              size="small"
              name="first_name"
              value={formData.first_name}
              onChange={handleInputChange}
              error={!!formErrors.first_name}
              helperText={formErrors.first_name}
            />
            <TextField
              label="Last Name*"
              variant="outlined"
              fullWidth
              margin="normal"
              size="small"
              name="last_name"
              value={formData.last_name}
              onChange={handleInputChange}
              error={!!formErrors.last_name}
              helperText={formErrors.last_name}
            />
            <TextField
              label="Email ID*"
              variant="outlined"
              type="email"
              size="small"
              fullWidth
              margin="normal"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              error={!!formErrors.email}
              helperText={formErrors.email}
            />
            <TextField
              label="Phone Number*"
              variant="outlined"
              type="tel"
              fullWidth
              margin="normal"
              size="small"
              name="phone_number"
              value={formData.phone_number}
              onChange={handleInputChange}
              error={!!formErrors.phone_number}
              helperText={formErrors.phone_number}
            />
            <TextField
              select
              label="Subject"
              variant="outlined"
              fullWidth
              size="small"
              margin="normal"
              name="subject"
              value={formData.subject}
              onChange={handleInputChange}
            >
              <MenuItem value="General Inquiry">General Inquiry</MenuItem>
              <MenuItem value="Request a Quote">Request a Quote</MenuItem>
              <MenuItem value="Technical Support">Technical Support</MenuItem>
              <MenuItem value="Partnership Opportunities">
                Partnership Opportunities
              </MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </TextField>
            <Button
              className="GlobalButton"
              variant="contained"
              sx={{
                backgroundColor: `${contentButtonColor}!important`,
                color: `${contentButtonTextColor}!important`,
                marginTop: "2%",
              }}
              type="submit"
              fullWidth
            >
              Submit
            </Button>
          </form>
        </Grid>
      </Grid>
      <Grid container spacing={3} maxWidth="lg">
        
       
      </Grid>
      
    </Box>
  );
};

export default ContactUsTheme;
